import { useEffect, useState } from 'react';
import { useQuery } from 'data-fetching-client';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import BrandVoiceHelpIcon, { BrandVoicePopover } from './BrandVoiceHelpIcon';
import { GET_BRAND_VOICE_BY_BUSINESS_UNIT_ID } from '../queries/BrandingQueries';
import { DefaultBrandVoiceChannel } from '../constants/BrandVoice';
import { getBrandIdentityUrl } from '../utils/urls';
import { BrandIdentityTabs } from '../constants/urls';
import { getErrorIs404NotFound, reportErrorToRaven } from '../utils/errors';
import { ACCOUNT_BRAND_ID } from '../constants/Brands';
import { RefreshIcon } from 'foundations-assets/icons/components/RefreshIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BrandVoiceRefreshButton = ({
  refetchBrandVoice
}) => {
  const [isThrottled, setIsThrottled] = useState(false);
  const handleRefresh = () => {
    if (isThrottled) return;
    setIsThrottled(true);
    refetchBrandVoice();
    setTimeout(() => {
      setIsThrottled(false);
    }, 5000);
  };
  return /*#__PURE__*/_jsx(Tooltip, {
    title: isThrottled ? unescapedText('brandVoiceRefreshButton.throttled.tooltip') : unescapedText('brandVoiceRefreshButton.default.tooltip'),
    children: /*#__PURE__*/_jsx(Button, {
      marginInlineStart: "200",
      use: "link",
      onClick: handleRefresh,
      disabled: isThrottled,
      children: /*#__PURE__*/_jsx(RefreshIcon, {})
    })
  });
};
const BrandVoiceDisabledMessage = ({
  brandVoiceChannel,
  hydratedBrandSettingsUrl,
  refetchBrandVoice,
  config
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "small",
      use: "help",
      textWrap: "pretty",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `brandVoiceMessage.${brandVoiceChannel || DefaultBrandVoiceChannel}.disabled_jsx`,
        elements: {
          Link
        },
        options: {
          external: true,
          href: hydratedBrandSettingsUrl
        }
      })
    }), !(config !== null && config !== void 0 && config.hideRefreshButton) && /*#__PURE__*/_jsx(BrandVoiceRefreshButton, {
      refetchBrandVoice: refetchBrandVoice
    })]
  });
};
const BrandVoiceSetupMessage = ({
  brandVoiceChannel,
  hydratedBrandSettingsUrl,
  refetchBrandVoice,
  config
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "small",
      use: "help",
      textWrap: "pretty",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `brandVoiceMessage.${brandVoiceChannel || DefaultBrandVoiceChannel}.setup_jsx`,
        elements: {
          Link
        },
        options: {
          external: true,
          href: hydratedBrandSettingsUrl
        }
      })
    }), !(config !== null && config !== void 0 && config.hideRefreshButton) && /*#__PURE__*/_jsx(BrandVoiceRefreshButton, {
      refetchBrandVoice: refetchBrandVoice
    })]
  });
};
const BrandVoiceAppliedMessageWithPopover = ({
  brandVoiceChannel,
  hydratedBrandSettingsUrl,
  voiceData
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const togglePopoverOpen = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };
  return /*#__PURE__*/_jsx(BrandVoicePopover, {
    voiceChannel: brandVoiceChannel,
    voiceData: voiceData,
    hydratedBrandSettingsUrl: hydratedBrandSettingsUrl,
    open: isPopoverOpen,
    onOpenChange: togglePopoverOpen,
    children: /*#__PURE__*/_jsx(Link, {
      onClick: togglePopoverOpen,
      children: /*#__PURE__*/_jsx(Text, {
        as: "small",
        color: "text-interactive-default",
        textWrap: "pretty",
        children: unescapedText('brandVoiceMessage.copilot.brandIdentityApplied')
      })
    })
  });
};
const BrandVoiceAppliedMessage = ({
  brandVoiceChannel,
  hydratedBrandSettingsUrl,
  voiceData
}) => {
  return /*#__PURE__*/_jsx(BrandVoiceHelpIcon, {
    voiceChannel: brandVoiceChannel,
    voiceData: voiceData,
    hydratedBrandSettingsUrl: hydratedBrandSettingsUrl,
    children: /*#__PURE__*/_jsx(Text, {
      as: "small",
      use: "help",
      textWrap: "pretty",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `brandVoiceMessage.${brandVoiceChannel || DefaultBrandVoiceChannel}.applied_jsx`,
        elements: {
          Link
        },
        options: {
          external: true,
          href: hydratedBrandSettingsUrl
        }
      })
    })
  });
};
const BrandVoiceMessage = ({
  brandVoiceOptions,
  brandVoiceChannel,
  hasBrandVoiceScope,
  shouldRender,
  config,
  businessUnitId
}) => {
  var _brandVoiceError$mess, _brandVoiceData$chann;
  const shouldRenderMessage = Boolean(shouldRender !== null && shouldRender !== void 0 ? shouldRender : hasBrandVoiceScope);
  const {
    loading: brandVoiceLoading,
    data,
    error: brandVoiceError,
    refetch
  } = useQuery(GET_BRAND_VOICE_BY_BUSINESS_UNIT_ID, {
    skip: !shouldRenderMessage || !!brandVoiceOptions,
    variables: {
      businessUnitId: businessUnitId !== null && businessUnitId !== void 0 ? businessUnitId : ACCOUNT_BRAND_ID
    },
    refetchWritePolicy: 'overwrite'
  });
  const refetchBrandVoice = () => {
    refetch().catch(error => {
      if (error && !getErrorIs404NotFound(error)) {
        reportErrorToRaven(error, 'Failed to refetch brand voice in BrandVoiceMessage component');
      }
    });
  };
  useEffect(() => {
    if (brandVoiceError) {
      if (!getErrorIs404NotFound(brandVoiceError)) {
        reportErrorToRaven(brandVoiceError, 'Failed to fetch brand voice in BrandVoiceMessage component');
      }
    }
  }, [brandVoiceError]);
  const brandVoiceData = data === null || data === void 0 ? void 0 : data.fetchBrandVoiceByBusinessUnitId;
  const hasBrandVoiceSetUp = !!brandVoiceData;
  const requiresBrandVoiceSetup = !hasBrandVoiceSetUp && !!(brandVoiceError !== null && brandVoiceError !== void 0 && (_brandVoiceError$mess = brandVoiceError.message) !== null && _brandVoiceError$mess !== void 0 && _brandVoiceError$mess.includes('404'));
  // Enabled channels appear in the channelSettings array; disabled ones do not
  const hasBrandVoiceDisabledForChannel = !!brandVoiceChannel && !(brandVoiceData !== null && brandVoiceData !== void 0 && (_brandVoiceData$chann = brandVoiceData.channelSettings) !== null && _brandVoiceData$chann !== void 0 && _brandVoiceData$chann.includes(brandVoiceChannel));
  if (!shouldRenderMessage || brandVoiceLoading || !brandVoiceOptions && !data && !requiresBrandVoiceSetup) {
    return null;
  }
  const brandVoiceSetupUrl = getBrandIdentityUrl({
    tab: BrandIdentityTabs.OVERVIEW,
    businessUnitId
  });
  const channelOptimizationsUrl = getBrandIdentityUrl({
    tab: BrandIdentityTabs.CHANNELS,
    businessUnitId
  });
  if (requiresBrandVoiceSetup || brandVoiceOptions && !brandVoiceOptions.isSetUp) {
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "100",
      children: /*#__PURE__*/_jsx(BrandVoiceSetupMessage, {
        brandVoiceChannel: brandVoiceChannel,
        hydratedBrandSettingsUrl: brandVoiceSetupUrl,
        refetchBrandVoice: refetchBrandVoice,
        config: config === null || config === void 0 ? void 0 : config.setup
      })
    });
  }
  if (hasBrandVoiceSetUp && hasBrandVoiceDisabledForChannel || brandVoiceOptions && brandVoiceOptions.isSetUp && !brandVoiceOptions.isEnabledForApp) {
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "100",
      children: /*#__PURE__*/_jsx(BrandVoiceDisabledMessage, {
        brandVoiceChannel: brandVoiceChannel,
        hydratedBrandSettingsUrl: channelOptimizationsUrl,
        refetchBrandVoice: refetchBrandVoice,
        config: config === null || config === void 0 ? void 0 : config.disabled
      })
    });
  }
  const voiceData = (brandVoiceData === null || brandVoiceData === void 0 ? void 0 : brandVoiceData.voiceData) || (brandVoiceOptions === null || brandVoiceOptions === void 0 ? void 0 : brandVoiceOptions.voiceData);
  if (voiceData) {
    var _config$applied;
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "100",
      children: config !== null && config !== void 0 && (_config$applied = config.applied) !== null && _config$applied !== void 0 && _config$applied.asPopover ? /*#__PURE__*/_jsx(BrandVoiceAppliedMessageWithPopover, {
        brandVoiceChannel: brandVoiceChannel,
        hydratedBrandSettingsUrl: channelOptimizationsUrl,
        voiceData: voiceData
      }) : /*#__PURE__*/_jsx(BrandVoiceAppliedMessage, {
        brandVoiceChannel: brandVoiceChannel,
        hydratedBrandSettingsUrl: channelOptimizationsUrl,
        voiceData: voiceData
      })
    });
  }
};
export default BrandVoiceMessage;